import type { Tier } from '@/state/GameState';
import type { BookRaw } from './types';

const VALID_TIERS: ReadonlySet<Tier> = new Set<Tier>(['D', 'C', 'B', 'A', 'S']);

export type BokOverlay = {
  isbn: string;
  tittel?: string;
  author?: string;
  genre?: string;
  tier?: Tier;
  editorial_blurb?: string;
};

const STORAGE_KEY = 'forfatterspill:bokoverlay:v1';

export function lesOverlay(): BokOverlay[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is BokOverlay => erGyldig(item));
  } catch {
    return [];
  }
}

export function lagreOverlay(overlay: BokOverlay[]): void {
  if (typeof localStorage === 'undefined') return;
  const renset = overlay.filter((o) => erGyldig(o));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(renset));
}

export function fjernOverlay(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function anvendOverlay(books: BookRaw[], overlay: BokOverlay[]): BookRaw[] {
  if (overlay.length === 0) return books;
  const lookup = new Map<string, BokOverlay>();
  for (const o of overlay) {
    if (o.isbn) lookup.set(o.isbn, o);
  }
  return books.map((b) => {
    const isbn = (b.isbn ?? b.id ?? '').trim();
    const o = lookup.get(isbn);
    if (!o) return b;
    const merged: BookRaw = { ...b };
    if (typeof o.tittel === 'string' && o.tittel.trim().length > 0) {
      merged.title = o.tittel;
    }
    if (typeof o.author === 'string' && o.author.trim().length > 0) {
      merged.author = o.author;
    }
    if (typeof o.genre === 'string' && o.genre.trim().length > 0) {
      merged.genre = o.genre;
    }
    if (o.tier && VALID_TIERS.has(o.tier)) {
      merged.tier = o.tier;
    }
    if (typeof o.editorial_blurb === 'string' && o.editorial_blurb.trim().length > 0) {
      merged.editorial_blurb = o.editorial_blurb;
    }
    return merged;
  });
}

function erGyldig(o: unknown): o is BokOverlay {
  if (!o || typeof o !== 'object') return false;
  const obj = o as Record<string, unknown>;
  if (typeof obj.isbn !== 'string' || obj.isbn.trim().length === 0) return false;
  if (obj.tittel !== undefined && typeof obj.tittel !== 'string') return false;
  if (obj.author !== undefined && typeof obj.author !== 'string') return false;
  if (obj.genre !== undefined && typeof obj.genre !== 'string') return false;
  if (obj.editorial_blurb !== undefined && typeof obj.editorial_blurb !== 'string') {
    return false;
  }
  if (obj.tier !== undefined) {
    if (typeof obj.tier !== 'string') return false;
    if (!VALID_TIERS.has(obj.tier as Tier)) return false;
  }
  return true;
}
